import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const GREY_ZERO = '#cbd5e1';   // mai importata (0)
const GREY_LOW  = '#e8e5e0';   // dati insufficienti (<15 controllati)
const MIN_CTRL  = 15;

/* Colore per area in base a copertura/bontà. entry = {importati, controllati, bonta}. */
export function coverageColor(entry) {
  if (!entry || !entry.importati) return GREY_ZERO;
  if ((entry.controllati || 0) < MIN_CTRL) return GREY_LOW;
  const b = entry.bonta ?? 0;
  if (b < 40) return '#ef4444';
  if (b < 55) return '#f59e0b';
  if (b < 70) return '#eab308';
  return '#22c55e';
}

/* normalizza un nome area: niente accenti, prima parte dei nomi doppi (slash),
   separatori/punteggiatura unificati a spazio, maiuscolo. */
function norm(s) {
  return (s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .split('/')[0]
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/* match nome area (provincia o regione) -> chiave in data (nomi maiuscoli). */
function matchKey(feature, data) {
  const p = feature.properties || {};
  const raw = norm(p.prov_name || p.DEN_UTS || p.NAME_2 || p.reg_name || p.NAME_1 || '');
  if (!raw) return null;
  for (const k of Object.keys(data)) {
    if (norm(k) === raw) return k;
  }
  for (const k of Object.keys(data)) {
    const nk = norm(k);
    if (nk.length >= 4 && (nk.includes(raw) || raw.includes(nk))) return k;
  }
  return null;
}

/* Geometria GeoJSON (Polygon/MultiPolygon) -> path SVG normalizzato in un viewBox 0..100,
   con aspect-ratio preservato (per la sagoma "serigrafata" nella scheda a destra). */
function regionSvgPath(geometry, size = 100, pad = 6) {
  if (!geometry) return null;
  const polys = geometry.type === 'Polygon' ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon' ? geometry.coordinates : null;
  if (!polys) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  polys.forEach((rings) => rings.forEach((ring) => ring.forEach(([lon, lat]) => {
    if (lon < minX) minX = lon; if (lon > maxX) maxX = lon;
    if (lat < minY) minY = lat; if (lat > maxY) maxY = lat;
  })));
  const bw = (maxX - minX) || 1, bh = (maxY - minY) || 1;
  const s = Math.min((size - 2 * pad) / bw, (size - 2 * pad) / bh);
  const offX = (size - bw * s) / 2, offY = (size - bh * s) / 2;
  const px = (lon) => (offX + (lon - minX) * s).toFixed(2);
  const py = (lat) => (offY + (maxY - lat) * s).toFixed(2);   // flip verticale
  let d = '';
  polys.forEach((rings) => rings.forEach((ring) => {
    ring.forEach(([lon, lat], i) => { d += (i === 0 ? 'M' : 'L') + px(lon) + ' ' + py(lat) + ' '; });
    d += 'Z ';
  }));
  return d.trim();
}

export default function CoperturaMap({ mode = 'province', data = {}, onAreaSelect }) {
  const [geojson, setGeojson] = useState(null);
  const hlRef = useRef(null);   // layer SELEZIONATO (col click): bordo spesso, resta fisso
  useEffect(() => {
    let ignore = false;
    setGeojson(null);
    const url = mode === 'regioni' ? '/italy-regions.geojson' : '/italy-provinces.geojson';
    fetch(url).then(r => r.json()).then(g => { if (!ignore) setGeojson(g); }).catch(e => console.error('GeoJSON', e));
    return () => { ignore = true; };
  }, [mode]);

  // stabile (dipende solo dai dati): evita che react-leaflet ri-applichi lo stile base
  // a ogni re-render, cancellando la selezione grigia impostata a mano.
  const featureStyle = useCallback((feature) => {
    const key = matchKey(feature, data);
    return {
      fillColor: coverageColor(key ? data[key] : null),
      fillOpacity: 0.85, color: '#999', weight: 1,
    };
  }, [data]);

  const onEachFeature = (feature, layer) => {
    const p = feature.properties || {};
    const raw = p.prov_name || p.DEN_UTS || p.NAME_2 || p.reg_name || p.NAME_1 || 'N/D';
    const key = matchKey(feature, data);
    const d = key ? data[key] : null;
    const bontaTxt = !d ? 'n/d' : ((d.controllati || 0) < MIN_CTRL ? 'n/d' : ((d.bonta ?? 0) + '%'));
    // le info NON vanno più in un tooltip sopra la regione: le passiamo al pannello a destra
    const info = {
      raw,
      vuota: !d || !d.importati,
      importati: d ? (d.importati || 0) : 0,
      controllati: d ? (d.controllati || 0) : 0,
      non_tim: d ? (d.non_tim || 0) : 0,
      bonta: bontaTxt,
      shape: regionSvgPath(feature.geometry),   // sagoma per la scheda a destra
    };
    const STILE_SEL = { fillColor: '#b3bbc7', fillOpacity: 0.92, color: '#8b93a1', weight: 1.2 };  // SELEZIONATA: riempita grigio chiaro
    const STILE_HOV = { color: '#0B5794', weight: 2 };    // solo passaggio mouse: bordo sottile
    // CLICK = seleziona subito la regione cliccata (anche se un'altra era già selezionata):
    // ripristina quella vecchia e riempie di grigio questa. NIENTE bringToFront qui, così la
    // selezionata non "cattura" il primo click quando ne clicchi un'altra ai bordi.
    layer.on('click', () => {
      const prev = hlRef.current;
      if (prev && prev !== layer) { try { prev.setStyle(featureStyle(prev.feature)); } catch (e) { /* rimosso */ } }
      layer.setStyle(STILE_SEL);
      hlRef.current = layer;
      if (onAreaSelect) onAreaSelect(info, key || raw);
    });
    // HOVER = solo anteprima (bordo sottile), NON cambia selezione/pannello/tabella
    layer.on('mouseover', () => {
      if (layer === hlRef.current) return;               // già selezionata: resta spessa
      layer.setStyle(STILE_HOV); layer.bringToFront();
    });
    layer.on('mouseout', () => {
      if (layer === hlRef.current) return;               // selezionata: non toccarla
      try { layer.setStyle(featureStyle(feature)); } catch (e) { /* rimosso */ }
    });
  };

  if (!geojson) {
    return <div style={{ height: '100%', minHeight: 320, borderRadius: 8, background: '#fafaf5',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', border: '1px solid #e8e5e0' }}>
      Caricamento mappa…</div>;
  }
  return (
    <MapContainer center={[41.7, 12.5]} zoom={5} scrollWheelZoom={false} zoomControl={false}
      dragging={false} doubleClickZoom={false} attributionControl={false}
      style={{ height: '100%', minHeight: 320, borderRadius: 8, background: '#fafaf5' }}>
      <GeoJSON key={mode + JSON.stringify(data)} data={geojson} style={featureStyle} onEachFeature={onEachFeature} />
    </MapContainer>
  );
}
